import WideSecureLayout from "../../layout/WideSecureLayout.jsx";
import {useTranslation} from "react-i18next";
import React, {useContext, useEffect, useMemo, useState} from "react";
import DateRangePicker from "../../components/DateRangePicker.jsx";
import UpdateDropdown from "../../components/dashboard/UpdateDropdown.jsx";
import {ErpCalculationApi} from "../../api/authority/generated/index";
import toast from "react-hot-toast";
import {useErrorTranslation} from "../../api/utilities/translateErrorMessage.js";
import {FilterContext} from "../../components/filters/DashboardFilterContextProvider.jsx";
import useAuthorityConfiguration from "../../api/utilities/useAuthorityConfiguration.js";
import {useRenderCurrencyValue, useRenderNumber, useRenderPercentValue} from "../../api/utilities/useValueRenderers.js";
import useUserSettings from "../../utils/useUserSettings.js";
import MarketingAndManagerSwitch from "../../components/filters/MarketingAndManagerSwitch.jsx";
import SeeMore from "../../components/SeeMore.jsx";
import LoadingCard from "../../components/LoadingCard.jsx";
import OverviewValue from "../../components/dashboard/OverviewValue.jsx";
import {GraphScale, GraphType} from "../../components/graphs/Graph.types.ts";
import DataGrid2 from "../../components/datagrid/DataGrid2.jsx";
import {useGetBackgroundColor,useGetBackgroundHoverColor,getPieColor} from "../../utils/graphColors.js";
import {useBrands} from "../../utils/brands/branding.js";
import BrandLogo from "../../utils/brands/BrandLogo.jsx";
import LineChart from "../../components/graphs/LineChart.jsx";
import PieChart from "../../components/graphs/PieChart.jsx";
import BarChart from "../../components/graphs/BarChart.jsx";
import {SnapshotCompletenessNotice} from "./ReportingLab.jsx";
import {shippingMethodDisplayName} from "../../utils/shippingMethodPresentation.js";

export default function NewComponents({project}) {

    let {t} = useTranslation();
    let {et} = useErrorTranslation();

    let {
        since, until,
    } = useContext(FilterContext);

    let authorityConfiguration = useAuthorityConfiguration();

    let renderCurrencyValue = useRenderCurrencyValue();
    let renderPercentValue = useRenderPercentValue();
    let renderNumber = useRenderNumber();

    let getBackgroundColor = useGetBackgroundColor();
    let getBackgroundHoverColor = useGetBackgroundHoverColor();

    let [userSettings] = useUserSettings();

    let [shopBasicStatisticsData, setShopBasicStatisticsData] = useState(null);
    let [turnoverByDomainData, setTurnoverByDomainData] = useState(null);
    let [statisticsBySourceData, setStatisticsBySourceData] = useState(null);
    let [snapshots, setSnapshots] = useState([]);
    let [countryShippingAndPaymentData, setCountryShippingAndPaymentData] = useState(null);
    let sinceKey = since.format('YYYY-MM-DD');
    let untilKey = until.format('YYYY-MM-DD');

    let brands = useBrands();

    useEffect(() => {
        let active = true;

        setShopBasicStatisticsData(null);
        setTurnoverByDomainData(null);
        setStatisticsBySourceData(null);
        setSnapshots([]);
        new ErpCalculationApi(authorityConfiguration)
            .reportingLegacyMarketingAndSales({
                projectId: project.id,
                erpFilter: {
                    since: sinceKey,
                    until: untilKey,
                    selectedDomainIds: [],
                    selectedCentreIds: [],

                }
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setShopBasicStatisticsData(nextData.basic);
                setTurnoverByDomainData(nextData.turnoverByDomain);
                setStatisticsBySourceData(nextData.statisticsBySource);
                setSnapshots([nextData.currentSnapshot, nextData.previousSnapshot]);
            })
            .catch((e) => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project.id, sinceKey, untilKey]);

    useEffect(() => {
        let active = true;

        setCountryShippingAndPaymentData(null);
        new ErpCalculationApi(authorityConfiguration)
            .shopCountryPaymentAndShippingStatistics({
                projectId: project.id, dateRangeInput: {
                    since: sinceKey, until: untilKey,
                }
            })
            .then((nextData) => {
                if (!active) {
                    return;
                }

                setCountryShippingAndPaymentData(nextData);
            })
            .catch((e) => {
                if (!active) {
                    return;
                }

                et(e).then((message) => { if (message) toast.error(message); });
            });

        return () => {
            active = false;
        };
    }, [project.id, sinceKey, untilKey]);

    /**
     * @type {GraphData|null}
     */
    let turnoverByDomainInTimeBarChartData = useMemo(() => {
        if (turnoverByDomainData === null) {
            return null;
        }

        let firstDomain = turnoverByDomainData.dataByDomain[0];

        if (firstDomain === null) {
            return null;
        }

        let labels = Object.keys(firstDomain.turnoverNetGranular);

        return {
            labels: labels,
            type: GraphType.Bar,
            scale: GraphScale.Linear,
            datasets: Object.values(turnoverByDomainData.dataByDomain).map(domain => {
                let selectedData = userSettings?.isMarketing ? domain.turnoverGrossGranular : domain.turnoverNetGranular;

                let values = Object.values(selectedData);
                let keys = Object.keys(selectedData);

                let data = [];
                for (let index in values) {
                    if (values[index].amount > 0) {
                        data.push({
                            key:  labels[index],
                            title:  keys[index],
                            value: values[index].amount / 100,
                            currency: project.currency,
                            type: 'money',
                        });
                    }
                }

                return {
                    label: domain.projectDomain.hostName,
                    data: data,
                    parsing: {
                        xAxisKey: 'key',
                        yAxisKey: 'value'
                    }
                };
            })
                .filter((dataset) => {
                    const sum = Object.values(dataset.data).reduce((acc, value) => acc + value, 0);
                    return sum !== 0;
                })
        };
    }, [turnoverByDomainData, userSettings?.isMarketing]);

    /**
     * @type {{source: object, graph: GraphData}[]|null}
     */
    let topChannelGraphDataList = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        const sources = [
            statisticsBySourceData.topChannel1,
            statisticsBySourceData.topChannel2,
            statisticsBySourceData.topChannel3,
            statisticsBySourceData.otherChannels,
        ];

        const data = [];

        sources.map((source) => {
            const labels = Object.keys(source.spendMarketing.valueGranular);

            data.push({
                source: source,
                graph: {
                    labels: labels,
                    type: GraphType.Line,
                    scale: GraphScale.Linear,
                    showLegend: false,
                    showTooltip: true,
                    interaction: {
                        intersect: false,
                        mode: 'nearest',
                    },
                    maintainAspectRatio: false,
                    datasets: [
                        source.spendMarketing.valueGranular,
                        source.spendMarketing.valueGranularPreviousPeriod,
                    ].map((granularData, index) => {
                        let isCurrent = index === 0;

                        let selectedDataValues = [];

                        const values = Object.values(granularData);
                        const keys = Object.keys(granularData);

                        for (let granularIndex in values) {
                            const label = labels[granularIndex];
                            selectedDataValues.push({
                                key:  label,
                                title:  keys[granularIndex],
                                value: values[granularIndex].amount / 100,
                                currency: project.currency,
                                type: 'money',
                            });
                        }

                        return {
                            label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                            data: selectedDataValues,
                            gray: isCurrent === false,
                            parsing: {
                                xAxisKey: 'key',
                                yAxisKey: 'value'
                            }
                        };
                    })
                },
            });
        });

        return data;
    }, [statisticsBySourceData, userSettings?.isMarketing]);

    /**
     * @type {GraphData|null}
     */
    let turnoverInTimeLineChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = userSettings?.isMarketing
            ? shopBasicStatisticsData.turnoverGross.valueGranular
            : shopBasicStatisticsData.turnoverNet.valueGranular;

        let selectedDataPreviousPeriod = userSettings?.isMarketing
            ? shopBasicStatisticsData.turnoverGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.turnoverNet.valueGranularPreviousPeriod;

        let labels = Object.keys(selectedData);

        var datasets = [selectedData, selectedDataPreviousPeriod].map((valueGranular, index) => {
            let isCurrent = index === 0;

            let values = Object.values(valueGranular);
            let keys = Object.keys(valueGranular);

            let data = [];
            for (let index in values) {
                const label = labels[index];

                data.push({
                    key:  label,
                    title:  keys[index],
                    value: values[index].amount / 100,
                    currency: project.currency,
                    type: 'money',
                });
            }
            return {
                label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                data: data,
                gray: isCurrent === false,
                parsing: {
                    xAxisKey: 'key',
                    yAxisKey: 'value'
                }
            };
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, userSettings?.isMarketing]);

    /**
     * @type {GraphData|null}
     */
    let pnoKpiChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = userSettings?.isMarketing
            ? shopBasicStatisticsData.pnoGross.valueGranular
            : shopBasicStatisticsData.pnoNet.valueGranular;

        let selectedDataPreviousPeriod = userSettings?.isMarketing
            ? shopBasicStatisticsData.pnoGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.pnoNet.valueGranularPreviousPeriod;

        let labels = Object.keys(selectedData);

        var datasets = [selectedData, selectedDataPreviousPeriod].map((valueGranular, index) => {
            let isCurrent = index === 0;

            let values = Object.values(valueGranular);
            let keys = Object.keys(valueGranular);

            let data = [];
            for (let index in values) {
                const label = labels[index];

                data.push({
                    key:  label,
                    title:  keys[index],
                    value: values[index],
                    currency: null,
                    type: 'percentage',
                });
            }
            return {
                label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                data: data,
                gray: isCurrent === false,
                parsing: {
                    xAxisKey: 'key',
                    yAxisKey: 'value'
                }
            };
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, userSettings?.isMarketing]);

    /**
     * @type {GraphData|null}
     */
    let aovKpiChartData = useMemo(() => {
        if (shopBasicStatisticsData === null) {
            return null;
        }

        let selectedData = userSettings?.isMarketing
            ? shopBasicStatisticsData.averageOrderValueGross.valueGranular
            : shopBasicStatisticsData.averageOrderValueNet.valueGranular;

        let selectedDataPreviousPeriod = userSettings?.isMarketing
            ? shopBasicStatisticsData.averageOrderValueGross.valueGranularPreviousPeriod
            : shopBasicStatisticsData.averageOrderValueNet.valueGranularPreviousPeriod;

        let labels = Object.keys(selectedData);

        var datasets = [selectedData, selectedDataPreviousPeriod].map((valueGranular, index) => {
            let isCurrent = index === 0;

            let values = Object.values(valueGranular);
            let keys = Object.keys(valueGranular);

            let data = [];
            for (let index in values) {
                const label = labels[index];

                data.push({
                    key:  label,
                    title:  keys[index],
                    value: values[index].amount / 100,
                    currency: project.currency,
                    type: 'money',
                });
            }
            return {
                label: isCurrent ? t('common.current-value') : t('common.previous-period-value'),
                data: data,
                gray: isCurrent === false,
                parsing: {
                    xAxisKey: 'key',
                    yAxisKey: 'value'
                }
            };
        });

        return {
            labels: labels,
            type: GraphType.Line,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: datasets,
        };
    }, [shopBasicStatisticsData, userSettings?.isMarketing]);

    let spendOverviewSourceData = useMemo(() => {
        if (statisticsBySourceData === null) {
            return null;
        }

        return [
            ...Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories),
            ...Object.values(statisticsBySourceData.topOtherCategoriesChannels),
        ];
    }, [statisticsBySourceData, userSettings?.isMarketing]);

    let spendComparisonGraphData = useMemo(() => {
        if (!statisticsBySourceData) return null;

        const sources = [
            ...Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories),
            ...Object.values(statisticsBySourceData.topOtherCategoriesChannels),
        ];

        const labels = [];
        const dataSource = {
            data: [],
            backgroundColor: [],
            hoverBackgroundColor: [],
            borderWidth: 0,
        };

        sources.forEach((source, idx) => {
            const amount = parseFloat(source.spendMarketing.value.amount);
            if (amount <= 0) return;
            labels.push(source.sourceName);
            dataSource.data.push(amount / 100);
            dataSource.backgroundColor.push(getPieColor(idx));
            dataSource.hoverBackgroundColor.push(getPieColor(idx, 'hover'));
        });

        return {
            labels,
            type: GraphType.Pie,
            scale: GraphScale.Linear,
            showLegend: false,
            datasets: [dataSource],
        };
    }, [statisticsBySourceData, userSettings?.isMarketing]);

    return <WideSecureLayout
        title={t('route.marketing-and-sales')}
        actions={<div className={'filter-btn flex justify-center gap-1 flex-wrap sm:justify-end'}>
            <UpdateDropdown/>
            <MarketingAndManagerSwitch/>
            <DateRangePicker/>
        </div>}
    >
        <SnapshotCompletenessNotice snapshots={snapshots} className="mt-6"/>

        <div className={'flex gap-6 w-full'}>
            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.base-statistical-data')}
                className={'mt-6 w-8/12 flex flex-col'}
                childrenClassName={'px-4 py-2 h-full flex flex-col grow-1'}
            >
                <div className={'flex flex-col justify-between grow-1'}>
                    {shopBasicStatisticsData && <div className="flex flex-wrap">
                        <OverviewValue
                            title={t('orders.turnover')}
                            titleSize={'2xl'}
                            value={userSettings?.isMarketing ? renderCurrencyValue(shopBasicStatisticsData.turnoverGross.value, true) : renderCurrencyValue(shopBasicStatisticsData.turnoverNet.value, true)}
                            percentChange={userSettings?.isMarketing ? shopBasicStatisticsData.turnoverGross.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.turnoverNet.percentageChangeAgainstPreviousPeriod}
                        />
                        <OverviewValue
                            title={t('dashboard.grid.profit')}
                            titleSize={'2xl'}
                            value={userSettings?.isMarketing ? renderCurrencyValue(shopBasicStatisticsData.gainGross.value, true) : renderCurrencyValue(shopBasicStatisticsData.gainNet.value, true)}
                            percentChange={userSettings?.isMarketing ? shopBasicStatisticsData.gainGross.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.gainGross.percentageChangeAgainstPreviousPeriod}
                        />
                        <OverviewValue
                            title={t('route.marketing')}
                            titleSize={'2xl'}
                            value={renderCurrencyValue(shopBasicStatisticsData.spendMarketing.value, true)}
                            percentChange={shopBasicStatisticsData.spendMarketing.percentageChangeAgainstPreviousPeriod}
                        />
                        <OverviewValue
                            title={t('dashboard.orders-short')}
                            titleSize={'2xl'}
                            value={userSettings?.isMarketing ? renderNumber(shopBasicStatisticsData.orderAmountGross.value) : renderNumber(shopBasicStatisticsData.orderAmountNet.value)}
                            percentChange={userSettings?.isMarketing ? shopBasicStatisticsData.orderAmountGross.percentageChangeAgainstPreviousPeriod : shopBasicStatisticsData.orderAmountNet.percentageChangeAgainstPreviousPeriod}
                            last={true}
                        />
                    </div>}
                    {turnoverInTimeLineChartData && <LineChart
                        data={turnoverInTimeLineChartData}
                        style={{height: '230px', maxHeight: '230px'}}
                    />}
                </div>
            </LoadingCard>

            <LoadingCard
                loading={shopBasicStatisticsData === null}
                title={t('project.kpi')}
                className={'mt-6 w-4/12'}
            >
                {shopBasicStatisticsData && <div className="flex flex-col flex-wrap">
                    <OverviewValue
                        title={t('dashboard.total-shop-pno')}
                        value={renderPercentValue(userSettings?.isMarketing ? shopBasicStatisticsData.pnoGross.value : shopBasicStatisticsData.pnoNet.value)}
                        last={true}
                    />
                    {pnoKpiChartData && <LineChart
                        data={pnoKpiChartData}
                        style={{'maxHeight': '100px'}}
                    />}
                    <OverviewValue
                        title={'AOV'}
                        value={renderCurrencyValue(userSettings?.isMarketing ? shopBasicStatisticsData.averageOrderValueGross.value : shopBasicStatisticsData.averageOrderValueNet.value)}
                        last={true}
                    />
                    {aovKpiChartData && <LineChart
                        data={aovKpiChartData}
                        style={{'maxHeight': '100px'}}
                    />}
                </div>}
            </LoadingCard>
        </div>
        <div className={'flex gap-6 w-full col-channels'}>
            {[0, 1, 2, 3].map((index) =>
                <LoadingCard
                    loading={topChannelGraphDataList === null}
                    className={'mt-6 w-1/4'}
                    key={'sourceGraph' + index}
                >
                    {topChannelGraphDataList && topChannelGraphDataList[index] && <>
                        <OverviewValue
                            title={topChannelGraphDataList[index].source.sourceName}
                            value={renderCurrencyValue(topChannelGraphDataList[index].source.spendMarketing.value, true)}
                            last={true}
                        />
                        <LineChart
                            data={topChannelGraphDataList[index].graph}
                            style={{'maxHeight': '100px'}}
                        />
                    </>}
                </LoadingCard>
            )}
        </div>
        <div className={'flex gap-6 w-full col-turnover-in-time'}>
            <LoadingCard
                loading={turnoverByDomainData === null}
                title={t('project.turnover-in-time')}
                className={'mt-6 w-full h-[382px]'}
            >
                {turnoverByDomainInTimeBarChartData && <BarChart
                    data={turnoverByDomainInTimeBarChartData}
                    style={{height: '300px', maxHeight: '300px'}}
                />}
            </LoadingCard>
        </div>

        <div className={'flex gap-6 w-full mt-6'}>
            <LoadingCard
                loading={statisticsBySourceData === null}
                className={'w-8/12'}
                title={t('orders.top-channels')}
            >
                {statisticsBySourceData && <SeeMore heightClass={'h-60'}>
                    <DataGrid2
                        columns={[{
                            name: 'sourceName', header: t('dashboard.channel'), defaultFlex: 2,
                        }, {
                            name: 'orderAmount',
                            textAlign: 'end',
                            header: t('dashboard.grid.spend-marketing'),
                            render: (row) => userSettings?.isMarketing ? row.data.orderAmountGross.value : row.data.orderAmountNet.value,
                        }, {
                            name: 'averageOrderValue', textAlign: 'end', header: 'AOV', render: (row) => {
                                return renderCurrencyValue(userSettings?.isMarketing ? row.data.averageOrderValueNet.value : row.data.averageOrderValueNet.value, true,);
                            },
                        }, {
                            name: 'spendMarketing',
                            textAlign: 'end',
                            header: t('dashboard.grid.spend-marketing'),
                            render: (row) => {
                                return renderCurrencyValue(row.data.spendMarketing.value, true,);
                            },
                        }, {
                            name: 'turnover', textAlign: 'end', header: t('dashboard.turnover'), render: (row) => {
                                return renderCurrencyValue(userSettings?.isMarketing ? row.data.turnoverGross.value : row.data.turnoverNet.value, true,);
                            },
                        }]}
                        dataSource={Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories)}
                        hasTotal={false}
                    />
                </SeeMore>}

            </LoadingCard>


            <LoadingCard
                loading={statisticsBySourceData === null}
                className={'w-4/12'}
                title={t('orders.top-other-channels')}
            >
                {statisticsBySourceData && <DataGrid2
                    columns={[{
                        name: 'sourceName', header: t('dashboard.channel'), defaultFlex: 1,
                    }, {
                        name: 'amount',
                        textAlign: 'end',
                        header: t('dashboard.grid.spend-marketing'),
                        render: (row) => {
                            return renderCurrencyValue(row.data.spendMarketing.value, true,);
                        },
                    }]}
                    dataSource={Object.values(statisticsBySourceData.topOtherCategoriesChannels)}
                />}
            </LoadingCard>
        </div>

        <div className={'flex gap-6 w-full mt-6'}>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-country')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[{
                            name: 'countryName',
                            header: t('orders.country'),
                            defaultFlex: 1,
                            render: (row) => row.data.country.name,
                            percentage: {
                                width: 50,
                                color: getBackgroundColor(12),
                            },
                        }, {
                            name: 'amount', textAlign: 'end', header: t('orders.turnover'), render: (row) => {
                                return userSettings?.isMarketing ? renderCurrencyValue(row.data.turnoverGross, true) : renderCurrencyValue(row.data.turnoverNet, true)
                            },
                        }]}
                        dataSource={countryShippingAndPaymentData.countryStatistics}
                        truncateFirstColumn={false}
                    />
                </SeeMore>}
            </LoadingCard>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-payment')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[{
                            name: 'paymentMethodName',
                            header: t('orders.payment-method'),
                            defaultFlex: 1,
                            render: (row) => row.data.paymentMethod.name,
                            percentage: {
                                width: 50,
                                color: getBackgroundColor(6),
                            },
                        }, {
                            name: 'amount', textAlign: 'end', header: t('orders.amount'), render: (row) => {
                                return userSettings?.isMarketing ? row.data.amountGross : row.data.amountNet
                            },
                        }]}
                        truncateFirstColumn={true}
                        dataSource={countryShippingAndPaymentData.paymentStatistics}
                    />
                </SeeMore>}
            </LoadingCard>
            <LoadingCard
                loading={countryShippingAndPaymentData === null}
                className={'w-4/12'}
                title={t('orders.orders-by-shipping')}
            >
                {countryShippingAndPaymentData && <SeeMore heightClass={'h-[223px]'}>
                    <DataGrid2
                        columns={[
                            {
                                name: 'shippingMethodText',
                                header: t('orders.shipping-method'),
                                defaultFlex: 1,
                                render: (row) => shippingMethodDisplayName(row.data.shippingMethod, t),
                                percentage: {
                                    width: 50,
                                    color: getBackgroundColor(0),
                                },
                            },
                            {
                                name: 'amount',
                                textAlign: 'end',
                                header: t('orders.amount'),
                                render: (row) => {
                                    return userSettings?.isMarketing ? row.data.amountGross : row.data.amountNet
                                },
                            }
                        ]
                        }
                        dataSource={countryShippingAndPaymentData.shippingStatistics}
                    />
                </SeeMore>}
            </LoadingCard>
        </div>


        <div className={'flex gap-6'}>
            <div className={'w-5/12 mt-6'}>
                <LoadingCard
                    loading={spendComparisonGraphData === null}
                    className={'w-full'}
                    title={t('orders.spend-comparison')}
                >
                    {project && spendComparisonGraphData && <PieChart
                        data={spendComparisonGraphData}
                        width={389}
                        height={250}
                        currency={project.currency}
                    />}
                </LoadingCard>
            </div>

            <div className={'w-7/12 mt-6'}>
                <LoadingCard
                    loading={spendOverviewSourceData === null}
                    className={'w-full'}
                    title={t('orders.spend-overview')}
                >
                    {spendOverviewSourceData && <SeeMore heightClass={'h-[264px]'}>
                        <DataGrid2
                            columns={[
                                {
                                    name: 'sourceName',
                                    header: t('dashboard.channel'),
                                    render: ({ data }) => {
                                        const sourceName = data.sourceName;
                                        const brandObj = brands[sourceName];  // This is the object from useBrands(), or undefined
                                        const displayName = brandObj?.name || sourceName;  // Use .name if object, else fallback to sourceName (string)
                                        return (
                                            <div className={'flex gap-2 items-center'}>
                                                <BrandLogo
                                                    brand={brandObj || sourceName}  // Pass object if exists, else string (adjust if BrandLogo requires object)
                                                    size={8}
                                                />
                                                {displayName}
                                            </div>
                                        );
                                    }
                                },
                                {
                                    name: 'spendMarketing',
                                    textAlign: 'end',
                                    header: t('dashboard.grid.spend-marketing'),
                                    render: (row) => {
                                        return renderCurrencyValue(row.data.spendMarketing.value, true,);
                                    },
                                },
                                {
                                    name: 'turnover',
                                    textAlign: 'end',
                                    header: t('dashboard.turnover'),
                                    render: (row) => {
                                        return renderCurrencyValue(userSettings?.isMarketing ? row.data.turnoverGross.value : row.data.turnoverNet.value, true,);
                                    },
                                }
                            ]}
                            dataSource={Object.values(statisticsBySourceData.topMarketingChannelsWithoutOtherCategories)}
                            hasTotal={false}
                            truncateFirstColumn={false}
                        />
                    </SeeMore>}

                </LoadingCard>
            </div>
        </div>
    </WideSecureLayout>;
}
